import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Map, Zap } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { getDisplayName } from '@/services/zklogin-auth';
import { apiFetch } from '@/services/api-client';

function Header() {
  const { logout, userAddress } = useAuthStore();

  // Same source as the dashboard's PLAYER row (PlayerName.tsx): the editable
  // display_name on user_profiles, read via /api/account profile-get. Falls
  // back to the Google given_name, then a shortened address, if unset.
  const [customName, setCustomName] = useState<string | null>(null);
  useEffect(() => {
    if (!userAddress) return;
    let dead = false;
    apiFetch<{ displayName: string | null }>('/api/account', { action: 'profile-get' })
      .then((r) => { if (!dead) setCustomName(r.displayName); })
      .catch(() => { /* falls back below */ });
    return () => { dead = true; };
  }, [userAddress]);

  const shortAddress = customName
    || getDisplayName()
    || (userAddress ? `${userAddress.slice(0, 6)}...${userAddress.slice(-4)}` : null);



  return (
    <header className="no-print">
      {/* ── Main Title Bar ── */}
      <div
        className="pixel-corners"
        // Horizontal margin removed — MainLayout now wraps Header in the same
        // `container mx-auto px-4` as page content, so the box's left/right
        // edges line up with the rounded-[10px] panels below it (mig 040 fix).
        style={{ margin: '16px 0 0', background: '#f96a55' }}
      >
        <div className="flex items-center justify-between p-4 md:p-5">
          {/* Left: Game Title */}
          <Link to="/" className="group flex items-center gap-3">
            <h1
              style={{
                fontSize: 'clamp(0.9rem, 3vw, 1.4rem)',
                color: '#fff',
                margin: 0,
                letterSpacing: '2px',
              }}
            >
              BIT BREW
            </h1>
          </Link>

          {/* Right: Level + Bean Counter */}
          <div className="flex items-center gap-4">
            {/* Navigate to Shops — "CHANGE SHOP" */}
            <Link
              to="/shops"
              className="pixel-corners bg-coral text-white font-grotesk font-bold text-sm py-3 px-6 hidden sm:flex items-center gap-2"
              style={{ padding: '8px 14px' }}
            >
              <Map size={14} />
              <span style={{ fontSize: '0.45rem' }}>CHANGE SHOP</span>
            </Link>

            {/* Log Out */}
            <button
              onClick={logout}
              className="pixel-corners bg-tab-inactive text-body-grey font-grotesk font-bold text-sm py-3 px-6 hover:bg-hairline flex items-center gap-2 min-h-[44px]"
              style={{
                padding: '12px 14px',
                color: '#e52222',
                borderColor: '#e52222',
                boxShadow: '3px 3px 0px #e52222',
              }}
            >
              <LogOut size={14} />
              <span className="hidden sm:inline" style={{ fontSize: '0.45rem' }}>LOG OUT</span>
            </button>
          </div>
        </div>

        {/* ── Player HUD (underneath title, like Retro Brew) ── */}
        {userAddress && (
          <div
            className="flex items-center gap-3 px-5 py-2 border-t"
            style={{
              borderColor: 'rgba(255,0,234,0.25)',
              background: 'rgba(0,0,0,0.08)',
              fontSize: '0.55rem',
            }}
          >
            <Zap size={10} style={{ color: '#ffcc00' }} />
            <span className="font-heading" style={{ color: '#ffcc00', fontSize: '0.45rem' }}>
              LOGGED IN
            </span>
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>│</span>
            <span className="font-body" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.55rem' }}>
              {shortAddress}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.2)' }}>│</span>
            <span className="font-heading" style={{ color: '#ffffff', fontSize: '0.5rem' }}>
              ONLINE
            </span>

            {/* Mobile: Change Shop — padded to a real thumb target */}
            <Link
              to="/shops"
              className="sm:hidden ml-auto flex items-center gap-1.5 py-3 px-2 -my-2"
              style={{ color: '#ffffff', fontSize: '0.6rem' }}
            >
              <Map size={12} />
              <span className="font-heading">SHOP</span>
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
